import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  AbstractControl,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { TypedFormBuilder } from '../../form/TypedFormBuilder';
import { AvailableCurrenciesService } from '../../../zalamo/currencies/available-currencies.service';
import { AbstractValueAccessor } from '../../../core/form/FormControl';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { unFormatNumber } from '../../products/product-editor/helpers';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { removeTypename } from 'ZalamoLibrary/src/lib/core/graphql/helpers';
import { ZalamoDialogService } from '@ui/zalamo-dialog/zalamo-dialog.service';

@Component({
    selector: 'lib-price-setter',
    templateUrl: './price-setter.component.html',
    styleUrls: ['./price-setter.component.scss'],
    providers: [
        { provide: MatFormFieldControl, useExisting: PriceSetterComponent },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PriceSetterComponent
  extends AbstractValueAccessor
  implements OnInit, Validator, MatFormFieldControl<PriceSetterComponent>
{
  static nextId = 0;
  @Input() pickCountry = false;
  @Input() allowEmpty = false;
  priceModel = this.fb.typedGroup<{
    price: string;
    code: string;
    country: string;
  }>({
    price: [
      0,
      [
        (control: AbstractControl) =>
          this.allowEmpty ? null : Validators.required(control),
        Validators.min(0),
      ],
    ],
    code: [this.currenciesService.getCurrentCurrency()],
    country: [null],
  });
  @Output() changed = new EventEmitter<
    Array<{ code: string; price: number; country: string }>
  >();
  lastKey;
  currentValue: Array<{ code: string; price: number; country: string }> = [];
  @Input() explainer = '';
  stateChanges = new Subject<void>();
  @HostBinding() id = `zalamo-price-setter-${PriceSetterComponent.nextId++}`;
  placeholder = '';
  empty;
  shouldLabelFloat;
  focused;
  required;
  disabled;
  errorState;
  @Output() onFocused = new EventEmitter();

  closePreview$ = new Subject();
  @Input() set current(current) {
    if (this.updateInProgress) {
      return;
    }
    if (/[.,]$/.test(this.lastKey)) {
      return;
    }
    for (let i = 0; i < current?.length; i++) {
      removeTypename(current[i]);
    }
    this.currentValue = current ? [...current] : [];
    const newPrice = this.currentValue.find(
      ({ code, country }) =>
        code === this.priceModel.value.code &&
        (this.priceModel.value.country === country ||
          !this.priceModel.value.country),
    );
    const patch: Partial<{
      price: string;
      code: string;
      country: string;
    }> = {
      price: String(newPrice?.price),
    };
    if (newPrice?.country) {
      patch.country = newPrice.country;
    }

    this.priceModel.patchValue(patch);
    this.stateChanges.next(undefined);
  }

  get invalid() {
    return this.priceModel.invalid;
  }

  updateInProgress = false;

  disableScroll(event: any) {
    event.preventDefault();
  }

  @ViewChild('preview', { static: true }) preview: TemplateRef<any>;

  constructor(
    private fb: TypedFormBuilder,
    private currenciesService: AvailableCurrenciesService,
    private elementRef: ElementRef,
    @Self() @Optional() public ngControl: NgControl,
    private zalamoDialogService: ZalamoDialogService,
  ) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  setDescribedByIds() {}

  onContainerClick() {}

  ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl?.control?.clearValidators();
    this.ngControl?.control?.setErrors(null);
    this.ngControl?.control?.updateValueAndValidity({
      emitEvent: false,
      onlySelf: true,
    });
    this.stateChanges.complete();
  }

  decrease() {
    const formattedPrice = Number(
      typeof this.priceModel.value.price === 'string'
        ? unFormatNumber(this.priceModel.value.price)
        : 0,
    );
    if (formattedPrice - 1 >= 0) {
      this.priceModel.patchValue({
        price: Intl.NumberFormat(undefined, {
          minimumFractionDigits: 2,
        }).format(Number(formattedPrice - 1) as any),
      });
    }
  }

  increase() {
    const formattedPrice = Number(
      typeof this.priceModel.value.price === 'string'
        ? this.priceModel.value.price.replace(',', '.').replace(/[^\d.-]/g, '')
        : 0,
    );
    this.priceModel.patchValue({
      price: Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
      }).format(Number(formattedPrice + 1) as any),
    });
  }

  codeUpdated(currency) {
    let price = '';
    const currentValue = this.currentValue?.find(
      ({ code, country }) =>
        code === currency &&
        (country === this.priceModel.value.country ||
          !this.priceModel.value.country),
    )?.price;
    if (typeof currentValue !== 'undefined') {
      price = this.currentValue.find(
        ({ code, country }) =>
          code === currency &&
          (country === this.priceModel.value.country ||
            !this.priceModel.value.country),
      )?.price as any;
    }
    this.priceModel.patchValue(
      {
        price,
      },
      { emitEvent: false },
    );
  }

  countryUpdated(currency) {
    let price = '';
    const currentValue = this.currentValue?.find(
      ({ country, code }) =>
        country === currency && code === this.priceModel.value.code,
    )?.price;
    if (typeof currentValue !== 'undefined') {
      price = this.currentValue.find(
        ({ country, code }) =>
          country === currency && code === this.priceModel.value.code,
      )?.price as any;
    }
    const code = this.currentValue.find(
      ({ country, code }) =>
        country === currency && code === this.priceModel.value.code,
    )?.code;
    const patch: Partial<{
      price: string;
      code: string;
      country: string;
    }> = {};
    if (price) {
      patch.price = price;
    }
    if (
      this.priceModel.value.code !== code ? code : this.priceModel.value.code
    ) {
      patch.code = code ? code : this.priceModel.value.code;
    }
    this.updateInProgress = true;
    this.priceModel.patchValue(patch);
    this.updateInProgress = false;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.allowEmpty && (!control.value || control.value.length === 0)) {
      return null;
    }
    if (
      this.allowEmpty &&
      control.value.some(
        ({ code, price }: { code: string; price: number }) =>
          !code?.trim() || !Number.isFinite(price) || price < 0,
      )
    ) {
      return { invalidPricing: true };
    }
    if (
      !control.value ||
      control.value?.length === 0 ||
      control.value?.some(
        ({ price }) =>
          price === null || (typeof price === 'string' && price.trim() === ''),
      )
    ) {
      return { missingPricing: true };
    }
    return null;
  }

  markAllAsTouched() {
    this.priceModel.markAllAsTouched();
    this.ngControl?.control?.updateValueAndValidity();
  }

  ngOnInit(): void {
    const control = this.ngControl?.control;
    const rootStatusChanges$ = control?.root?.statusChanges;

    if (control) {
      control.markAsTouched = () => {
        this.priceModel.markAllAsTouched();
        control.updateValueAndValidity();
      };
      control.markAllAsTouched = () => {
        this.priceModel.markAllAsTouched();
        // control.updateValueAndValidity();
      };
      control.setValidators([this.validate.bind(this)]);
      control.updateValueAndValidity({
        emitEvent: false,
        onlySelf: true,
      });
      if (control.parent) {
        this.priceModel.setParent(control.parent);
      }
    }

    const subscriptions = [
      rootStatusChanges$?.pipe(
        filter(() => ![',', '.'].includes(this.lastKey)),
        tap(() => {
          this.priceModel.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });
          this.priceModel.markAllAsTouched();
        }),
      ),
      this.valueSubject.pipe(
        filter(
          (v) =>
            !this.updateInProgress && !!v && ![',', '.'].includes(this.lastKey),
        ),
        tap((v) => {
          this.currentValue = [...v];
          this.updateInProgress = true;
          this.priceModel.controls.code.patchValue(
            this.priceModel.controls.code.value,
          );
          this.codeUpdated(this.priceModel.controls.code.value);

          this.updateInProgress = false;
          // this.ngControl.control.updateValueAndValidity();
        }),
      ),

      this.priceModel.controls.price.valueChanges.pipe(
        filter(
          (v) => !['.', ','].includes(this.lastKey) && !this.updateInProgress,
        ),
        distinctUntilChanged(),
        tap((price) => {
          this.updateInProgress = true;
          this.currentValue = !!this.currentValue ? [...this.currentValue] : [];
          let current = this.currentValue.findIndex(
            ({ code, country }) =>
              code === this.priceModel.value.code &&
              (!this.priceModel.value.country ||
                country === this.priceModel.value.country),
          );
          const cleared =
            price === null ||
            price === undefined ||
            (typeof price === 'string' && price.trim() === '');
          if (this.allowEmpty && cleared) {
            if (current !== -1) {
              this.currentValue.splice(current, 1);
            }
          } else {
            if (current === -1) {
              current =
                this.currentValue.push({
                  code: this.priceModel.value.code,
                  price: Number(this.priceModel.value.price),
                  country: this.priceModel.value.country,
                }) - 1;
            }

            this.currentValue[current] = {
              code: this.currentValue[current].code,
              price: unFormatNumber(price),
              country: this.currentValue[current].country,
            };
          }
          this.writeValue([...this.currentValue]);
          this.changed.emit([...this.currentValue]);
          this.updateInProgress = false;
        }),
      ),
      this.priceModel.controls.code.valueChanges.pipe(
        distinctUntilChanged(),
        tap(this.codeUpdated.bind(this)),
      ),
      this.priceModel.controls.country.valueChanges.pipe(
        distinctUntilChanged(),
        tap(this.countryUpdated.bind(this)),
      ),
    ].filter(Boolean);

    this.subscribeWhileAlive(...subscriptions);
  }

  filterKeys(event: KeyboardEvent) {
    if (event.key.length === 1 && /^[^0-9.,-]+$/.test(event.key)) {
      event.preventDefault();
    } else {
      this.lastKey = event.key;
    }
  }

  openPreview() {
    const ref = this.zalamoDialogService.openRight(this.preview);
    this.closePreview$
      .pipe(
        tap(() => ref.close()),
        takeUntil(ref.afterClosed()),
      )
      .subscribe();
  }

  removePrice(index) {
    this.currentValue.splice(index, 1);
    if (this.currentValue.length === 0) {
      this.priceModel.patchValue({
        price: '0',
      });
    } else {
      this.priceModel.patchValue({
        code: this.currentValue[0].code,
      });
      this.priceModel.patchValue({
        price: String(this.currentValue[0].price),
      });
    }
  }
}
